import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import * as React from "react";
import { cn } from "@/lib/utils";

const buttonVariants = cva(
  "inline-flex cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground hover:bg-zinc-800",
        secondary: "bg-secondary text-secondary-foreground hover:bg-zinc-200",
        outline:
          "border border-input bg-white hover:bg-zinc-50 hover:text-foreground",
        ghost: "hover:bg-zinc-100 hover:text-foreground",
        destructiveGhost:
          "text-destructive hover:bg-red-50 hover:text-red-700",
        accent: "bg-accent text-accent-foreground hover:bg-accent-hover",
        brand:
          "border-transparent text-white bg-paradigm-blue bg-[linear-gradient(135deg,#2563EB,#7C3AED)] hover:opacity-90",
        destructive:
          "bg-destructive text-white hover:bg-red-700",
      },
      size: {
        default: "h-9 px-4 py-2",
        sm: "h-9 rounded-md px-3 text-xs",
        lg: "h-11 rounded-md px-6",
        icon: "h-10 w-10",
      },
    },
    defaultVariants: {
      variant: "brand",
      size: "default",
    },
  },
);

function Button({
  className,
  variant,
  size,
  asChild = false,
  ...props
}: React.ComponentProps<"button"> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean;
  }) {
  const Comp = asChild ? Slot : "button";
  return (
    <Comp
      className={cn(buttonVariants({ variant, size }), className)}
      {...props}
    />
  );
}

export { Button, buttonVariants };
